import React, { useState } from "react";
import { Check, Star } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { PLANS, PRICING_TABLE, unitPrice } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

export default function Pricing() {
  const navigate = useNavigate();
  const [fleet, setFleet] = useState(3);

  return (
    <PublicLayout>
      <div className="lr-gradient-dark py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white">Abonnement mensuel par véhicule</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-white">Des tarifs simples, en MAD</h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">Choisissez l'offre adaptée à votre activité. Plus votre parc est grand, plus le prix par véhicule baisse.</p>
        </div>
      </div>

      {/* Calculator */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <label className="font-semibold text-slate-900">Taille de votre parc</label>
            <span className="font-display text-2xl font-extrabold" style={{ color: "#006233" }} data-testid="fleet-size-value">{fleet} véhicule(s)</span>
          </div>
          <Slider value={[fleet]} onValueChange={(v) => setFleet(v[0])} min={1} max={20} step={1} data-testid="fleet-size-slider" />
          <div className="mt-3 flex justify-between text-xs text-slate-400"><span>1</span><span>20+</span></div>
        </div>
      </div>

      {/* Plans */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const unit = unitPrice(plan.key, fleet);
            const total = unit * fleet;
            return (
              <div
                key={plan.key}
                data-testid={`plan-card-${plan.key}`}
                className={`relative rounded-3xl border bg-white p-7 flex flex-col ${plan.popular ? "border-transparent shadow-2xl ring-2" : "border-slate-200"}`}
                style={plan.popular ? { ["--tw-ring-color"]: "#006233", ringColor: "#006233" } : {}}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold text-white" style={{ background: "#006233" }}>
                    <Star className="h-3 w-3 fill-white" /> Le plus populaire
                  </span>
                )}
                <h3 className="font-display text-2xl font-extrabold" style={{ color: plan.color }}>{plan.name}</h3>
                <p className="text-sm text-slate-500 mt-1 italic">« {plan.tagline} »</p>
                <div className="mt-5">
                  <span className="font-display text-4xl font-extrabold text-slate-900">{unit}</span>
                  <span className="text-slate-500 font-medium"> MAD / véhicule / mois</span>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  Soit <strong className="text-slate-900">{total.toLocaleString()} MAD / mois</strong> pour {fleet} véhicule(s)
                </p>
                <ul className="mt-6 space-y-3 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#006233" }} /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full mt-7 h-11 font-semibold text-white"
                  style={{ background: plan.color }}
                  data-testid={`plan-subscribe-btn-${plan.key}`}
                  onClick={() => navigate("/register", { state: { plan: plan.key } })}
                >
                  Choisir {plan.name}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Full table */}
        <div className="mt-16">
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-6 text-center">Grille tarifaire complète (MAD / véhicule / mois)</h2>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="lr-gradient-dark text-white">
                <tr>
                  <th className="p-4 text-left">Parc auto</th>
                  <th className="p-4 text-center">Standard</th>
                  <th className="p-4 text-center">Pro</th>
                  <th className="p-4 text-center">Premium</th>
                </tr>
              </thead>
              <tbody>
                {PRICING_TABLE.map((r, i) => (
                  <tr key={r.range} className={i % 2 ? "bg-slate-50" : ""}>
                    <td className="p-4 font-semibold text-slate-900">{r.range}</td>
                    <td className="p-4 text-center">{r.standard} MAD</td>
                    <td className="p-4 text-center">{r.pro} MAD</td>
                    <td className="p-4 text-center">{r.premium} MAD</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-center text-xs text-slate-400 mt-4">Démonstration — aucun paiement n'est encaissé à ce stade.</p>
        </div>
      </div>
    </PublicLayout>
  );
}
